import { normalizeNickname, type GuestChallenge, type GuestSymbol } from "@endclock/shared";
import { solveGuestProof } from "./proof.js";

const SYMBOLS = { circle: "●", square: "■", triangle: "▲" } as const;
const ERRORS: Readonly<Record<string, string>> = {
  NICKNAME_IN_USE: "NICKNAME IN USE. Please choose another.",
  INVALID_NICKNAME: "Use 3–20 letters, numbers, underscores or hyphens.",
  RATE_LIMITED: "Too many attempts. Please wait a moment and try again.",
  CHALLENGE_INVALID: "Human check expired or was not accepted. Complete the new check.",
  CHALLENGE_CAPACITY: "Human checks are busy. Please wait a moment and retry.",
  CHALLENGE_EXPIRED: "Human check expired. Please complete the new check.",
  INVALID_CHALLENGE: "Human check was not accepted. Please try again.",
};

export class GuestGate {
  readonly element = document.createElement("section");
  private readonly form = document.createElement("form");
  private readonly nickname = document.createElement("input");
  private readonly choices = document.createElement("div");
  private readonly prompt = document.createElement("span");
  private readonly status = document.createElement("p");
  private readonly submit = document.createElement("button");
  private readonly refresh = document.createElement("button");
  private challenge: GuestChallenge | null = null;
  private answer: GuestChallenge["target"] | null = null;
  private controller: AbortController | null = null;
  private proof: Promise<number> | null = null;
  private resolve: ((nickname: string) => void) | null = null;
  private submitting = false;

  constructor(parent: HTMLElement, private readonly allowResume = true) {
    this.element.className = "guest-gate";
    this.element.id = "guest-gate";
    this.element.dataset.state = "checking";
    this.element.setAttribute("aria-labelledby", "guest-title");
    const brand = document.createElement("strong");
    brand.textContent = "ENDCLOCK";
    const heading = document.createElement("h1");
    heading.id = "guest-title";
    heading.textContent = "ENTER THE WORLD";
    const label = document.createElement("label");
    label.htmlFor = "guest-nickname";
    label.textContent = "NICKNAME";
    this.nickname.id = "guest-nickname";
    this.nickname.name = "nickname";
    this.nickname.minLength = 3;
    this.nickname.maxLength = 20;
    this.nickname.pattern = "[A-Za-z0-9_\\-]{3,20}";
    this.nickname.setAttribute("autocomplete", "nickname");
    this.nickname.autocapitalize = "off";
    this.nickname.spellcheck = false;
    this.nickname.required = true;
    const hint = document.createElement("small");
    hint.id = "guest-nickname-hint";
    hint.textContent = "3–20 letters, numbers, _ or -. No account needed.";
    this.nickname.setAttribute("aria-describedby", hint.id);
    const human = document.createElement("fieldset");
    const legend = document.createElement("legend");
    legend.textContent = "HUMAN CHECK";
    this.prompt.id = "guest-challenge-prompt";
    this.choices.className = "guest-symbols";
    this.choices.setAttribute("role", "group");
    this.choices.setAttribute("aria-labelledby", this.prompt.id);
    this.refresh.type = "button";
    this.refresh.textContent = "NEW CHECK";
    this.refresh.className = "guest-refresh";
    this.refresh.addEventListener("click", () => { void this.loadChallenge(); });
    human.append(legend, this.prompt, this.choices, this.refresh);
    const disclosure = document.createElement("p");
    disclosure.className = "guest-disclosure";
    disclosure.textContent = "Guest sessions and NPC conversations may be logged for testing and moderation.";
    this.status.setAttribute("role", "status");
    this.status.setAttribute("aria-live", "polite");
    this.status.id = "guest-status";
    this.submit.type = "submit";
    this.submit.textContent = "ENTER";
    this.submit.disabled = true;
    this.form.append(brand, heading, label, this.nickname, hint, human, disclosure, this.status, this.submit);
    this.form.addEventListener("submit", (event) => { event.preventDefault(); void this.start(); });
    this.element.append(this.form);
    parent.append(this.element);
    document.body.classList.add("guest-entry");
  }

  enter(): Promise<string> {
    const ready = new Promise<string>((resolve) => { this.resolve = resolve; });
    void this.resume();
    return ready;
  }

  private async resume(): Promise<void> {
    this.status.textContent = "Checking guest session…";
    try {
      const session = await request<{ active: boolean; nickname?: string }>("/api/guest/session");
      if (this.allowResume && session.active && session.nickname) { this.finish(session.nickname); return; }
    } catch { /* A fresh challenge provides a retry when initial session lookup fails. */ }
    await this.loadChallenge();
    this.nickname.focus();
  }

  private async loadChallenge(): Promise<void> {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.challenge = null;
    this.answer = null;
    this.proof = null;
    this.submit.disabled = true;
    this.choices.replaceChildren();
    this.status.textContent = "Loading human check…";
    this.element.dataset.state = "loading";
    this.refresh.disabled = true;
    try {
      const challenge = await request<GuestChallenge>("/api/guest/challenge", {});
      if (controller.signal.aborted) return;
      if (!(challenge.target in SYMBOLS) || !Array.isArray(challenge.symbols)
        || challenge.symbols.length !== 3 || !challenge.symbols.every((symbol) => symbol in SYMBOLS)) {
        throw new Error("Invalid human check. Please retry.");
      }
      this.challenge = challenge;
      this.prompt.textContent = `Select the ${challenge.target} ${SYMBOLS[challenge.target]}`;
      for (const symbol of challenge.symbols as readonly GuestSymbol[]) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = SYMBOLS[symbol];
        button.dataset.symbol = symbol;
        button.setAttribute("aria-label", symbol);
        button.setAttribute("aria-pressed", "false");
        button.addEventListener("click", () => {
          this.answer = symbol;
          for (const choice of this.choices.children) choice.setAttribute("aria-pressed", String(choice === button));
          this.submit.disabled = this.submitting;
          this.status.textContent = "Human check selected. Ready to enter.";
        });
        this.choices.append(button);
      }
      this.proof = solveGuestProof(challenge.nonce, challenge.difficulty, controller.signal);
      // Attach rejection immediately, even if the visitor never presses ENTER.
      void this.proof.catch(() => undefined);
      this.status.textContent = "Select the matching symbol.";
      this.element.dataset.state = "ready";
    } catch (error) {
      if (!controller.signal.aborted) this.status.textContent = readableError(error);
    } finally {
      if (!controller.signal.aborted) this.refresh.disabled = false;
    }
  }

  private async start(): Promise<void> {
    if (this.submitting || !this.challenge || !this.answer || !this.proof) return;
    const nickname = normalizeNickname(this.nickname.value);
    if (!nickname) {
      this.status.textContent = ERRORS.INVALID_NICKNAME!;
      return;
    }
    this.submitting = true;
    this.submit.disabled = true;
    this.refresh.disabled = true;
    this.status.textContent = "Preparing your guest session…";
    try {
      const counter = await this.proof;
      const session = await request<{ active: boolean; nickname: string }>("/api/guest/start", {
        nickname, challengeId: this.challenge.challengeId, answer: this.answer, counter,
      });
      if (!session.active || typeof session.nickname !== "string") throw new Error("Guest entry was not accepted.");
      this.finish(session.nickname);
    } catch (error) {
      const message = readableError(error);
      await this.loadChallenge();
      this.status.textContent = message;
    } finally {
      this.submitting = false;
      this.submit.disabled = !this.answer;
      this.refresh.disabled = false;
    }
  }

  private finish(nickname: string): void {
    this.controller?.abort();
    this.element.remove();
    document.body.classList.remove("guest-entry");
    this.resolve?.(nickname);
  }
}

async function request<T>(url: string, body?: object): Promise<T> {
  const response = await fetch(url, {
    method: body ? "POST" : "GET", credentials: "same-origin", cache: "no-store",
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  const value = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(ERRORS[value.error ?? ""] ?? "Guest entry unavailable. Please wait and retry.");
  return value;
}

function readableError(error: unknown): string {
  return error instanceof Error ? error.message : "Guest entry unavailable. Please retry.";
}
